import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PublicLayout } from "../components/layout/PublicLayout";
import { Card } from "../components/ui/Card";
import { Button } from "../components/ui/Button";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import type { TeamFitTraits, UserRole } from "../types";

const TRAIT_LABELS: { key: keyof TeamFitTraits; label: string; hint: string }[] = [
  { key: "communication", label: "Communication", hint: "Do you express your thoughts clearly?" },
  { key: "collaboration", label: "Collaboration", hint: "Do you prioritize team goals?" },
  { key: "leadership", label: "Leadership", hint: "Do you take initiative to lead?" },
  { key: "problemSolving", label: "Problem Solving", hint: "Do you approach blockers logically?" },
  { key: "adaptability", label: "Adaptability", hint: "Do you handle change flexibly?" },
  { key: "integrity", label: "Integrity", hint: "Do you maintain honest, consistent values?" },
];

function Bubble({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-start gap-2">
      <div className="mt-0.5 h-7 w-7 flex-shrink-0 rounded-full bg-gradient-to-br from-navy to-teal" />
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-slate-50 px-3.5 py-2.5 text-sm leading-relaxed text-navy">
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-xs font-medium text-slate-500">{label}</span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-navy placeholder:text-slate-300 focus:border-teal focus:outline-none";

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState<UserRole | null>(user?.role ?? null);
  const [step, setStep] = useState(user?.role ? 1 : 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Talent fields
  const [headline, setHeadline] = useState("");
  const [location, setLocation] = useState("");
  const [remotePreference, setRemotePreference] = useState("flexible");
  const [bio, setBio] = useState("");
  const [skillsInput, setSkillsInput] = useState("");
  const [careerGoal, setCareerGoal] = useState("");
  const [conflictStyle, setConflictStyle] = useState("Mediator");
  const [communicationStyle, setCommunicationStyle] = useState("Open & Horizontal");
  const [leadershipStyle, setLeadershipStyle] = useState("Supportive");
  const [traits, setTraits] = useState<TeamFitTraits>({
    communication: 70,
    collaboration: 70,
    leadership: 60,
    problemSolving: 70,
    adaptability: 70,
    integrity: 80,
  });

  // Company fields
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [cultureValuesInput, setCultureValuesInput] = useState("");
  const [workEnvironment, setWorkEnvironment] = useState("Remote / Hybrid");

  async function chooseRole(next: UserRole) {
    setRole(next);
    setSaving(true);
    setError(null);
    try {
      await api.post("/api/auth/role", { role: next });
      if (user) setUser({ ...user, role: next });
      setStep(1);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save role.");
    } finally {
      setSaving(false);
    }
  }

  async function submitTalent() {
    setSaving(true);
    setError(null);
    try {
      await api.put("/api/persona", {
        headline,
        location,
        remotePreference,
        bio,
        careerGoal,
        skills: skillsInput.split(",").map((s) => s.trim()).filter(Boolean),
        workStyle: { conflictStyle, communicationStyle, leadershipStyle },
        teamFitTraits: traits,
      });
      if (user) setUser({ ...user, onboarded: 1 });
      navigate("/app");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save profile.");
    } finally {
      setSaving(false);
    }
  }

  async function submitCompany() {
    setSaving(true);
    setError(null);
    try {
      await api.put("/api/company/me", {
        name: companyName,
        industry,
        cultureValues: cultureValuesInput.split(",").map((s) => s.trim()).filter(Boolean),
        workEnvironment,
        cultureTraits: traits,
      });
      if (user) setUser({ ...user, onboarded: 1 });
      navigate("/company");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save company profile.");
    } finally {
      setSaving(false);
    }
  }

  const totalSteps = 4;

  return (
    <PublicLayout>
      <div className="px-4 pb-16 pt-6">
        {role && (
          <div className="mb-5 h-1 w-full overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full bg-teal transition-all"
              style={{ width: `${(step / totalSteps) * 100}%` }}
            />
          </div>
        )}

        {step === 0 && (
          <>
            <Bubble>What brings you here? We'll get you started with the right service.</Bubble>
            <div className="mt-4 grid grid-cols-1 gap-3">
              <button
                onClick={() => chooseRole("talent")}
                className="rounded-2xl border border-slate-200 p-4 text-left active:border-teal"
              >
                <p className="text-sm font-semibold text-navy">🙋 I'm a Job Seeker</p>
                <p className="mt-1 text-xs text-slate-500">Find new opportunities and connect with the right team.</p>
              </button>
              <button
                onClick={() => chooseRole("company")}
                className="rounded-2xl border border-slate-200 p-4 text-left active:border-teal"
              >
                <p className="text-sm font-semibold text-navy">🏢 Company / HR</p>
                <p className="mt-1 text-xs text-slate-500">Find great talent that fits your team culture.</p>
              </button>
            </div>
          </>
        )}

        {role === "talent" && step === 1 && (
          <>
            <Bubble>Let's start with a brief introduction. What do you do?</Bubble>
            <Card className="border border-slate-100">
              <Field label="Headline (e.g. Product Designer)">
                <input className={inputClass} value={headline} onChange={(e) => setHeadline(e.target.value)} />
              </Field>
              <Field label="Location">
                <input className={inputClass} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Seoul, South Korea" />
              </Field>
              <Field label="Work Preference">
                <select className={inputClass} value={remotePreference} onChange={(e) => setRemotePreference(e.target.value)}>
                  <option value="remote">Remote</option>
                  <option value="hybrid">Hybrid</option>
                  <option value="onsite">On-site</option>
                  <option value="flexible">Flexible</option>
                </select>
              </Field>
              <Field label="Short Bio">
                <textarea className={inputClass} rows={3} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="I love turning complex problems into simple, beautiful experiences." />
              </Field>
            </Card>
            <StepFooter onNext={() => setStep(2)} disabled={!headline} />
          </>
        )}

        {role === "talent" && step === 2 && (
          <>
            <Bubble>Tell us about your core skills and career goals.</Bubble>
            <Card className="border border-slate-100">
              <Field label="Skills (comma separated)">
                <input className={inputClass} value={skillsInput} onChange={(e) => setSkillsInput(e.target.value)} placeholder="Figma, UX Research, Communication" />
              </Field>
              <Field label="Career Goal">
                <input className={inputClass} value={careerGoal} onChange={(e) => setCareerGoal(e.target.value)} placeholder="Global Product Designer" />
              </Field>
            </Card>
            <StepFooter onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </>
        )}

        {role === "talent" && step === 3 && (
          <>
            <Bubble>When your team faces a crisis, how do you respond?</Bubble>
            <Card className="border border-slate-100">
              <PillSelect label="Conflict Resolution Style" value={conflictStyle} onChange={setConflictStyle} options={["Mediator", "Direct", "Analytical", "Accommodating"]} />
              <PillSelect label="Communication Style" value={communicationStyle} onChange={setCommunicationStyle} options={["Open & Horizontal", "Structured", "Spontaneous", "Document-based"]} />
              <PillSelect label="Leadership Style" value={leadershipStyle} onChange={setLeadershipStyle} options={["Supportive", "Directive", "Delegative", "Coaching"]} />
            </Card>
            <StepFooter onBack={() => setStep(2)} onNext={() => setStep(4)} />
          </>
        )}

        {role === "talent" && step === 4 && (
          <>
            <Bubble>Last step — rate yourself. This will power your Team-fit Radar.</Bubble>
            <Card className="border border-slate-100">
              <TraitSliders traits={traits} onChange={setTraits} />
            </Card>
            {error && <p className="mt-2 text-xs text-coral">{error}</p>}
            <StepFooter onBack={() => setStep(3)} onNext={submitTalent} loading={saving} nextLabel="Complete Profile" />
          </>
        )}

        {role === "company" && step === 1 && (
          <>
            <Bubble>Tell us about your team.</Bubble>
            <Card className="border border-slate-100">
              <Field label="Company Name">
                <input className={inputClass} value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
              </Field>
              <Field label="Industry">
                <input className={inputClass} value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Global Tech Company" />
              </Field>
              <Field label="Work Environment">
                <input className={inputClass} value={workEnvironment} onChange={(e) => setWorkEnvironment(e.target.value)} placeholder="Remote / Hybrid" />
              </Field>
            </Card>
            <StepFooter onNext={() => setStep(2)} disabled={!companyName} />
          </>
        )}

        {role === "company" && step === 2 && (
          <>
            <Bubble>What values matter most to your team?</Bubble>
            <Card className="border border-slate-100">
              <Field label="Team Core Values (comma separated)">
                <input className={inputClass} value={cultureValuesInput} onChange={(e) => setCultureValuesInput(e.target.value)} placeholder="Innovation, Collaboration, Growth" />
              </Field>
            </Card>
            <StepFooter onBack={() => setStep(1)} onNext={() => setStep(3)} />
          </>
        )}

        {role === "company" && step === 3 && (
          <>
            <Bubble>Rate your team's work style from 0-100. This becomes your talent matching criteria.</Bubble>
            <Card className="border border-slate-100">
              <TraitSliders traits={traits} onChange={setTraits} />
            </Card>
            {error && <p className="mt-2 text-xs text-coral">{error}</p>}
            <StepFooter onBack={() => setStep(2)} onNext={submitCompany} loading={saving} nextLabel="Complete Team Culture Profile" />
          </>
        )}
      </div>
    </PublicLayout>
  );
}

function StepFooter({
  onBack,
  onNext,
  disabled,
  loading,
  nextLabel = "Next Step →",
}: {
  onBack?: () => void;
  onNext: () => void;
  disabled?: boolean;
  loading?: boolean;
  nextLabel?: string;
}) {
  return (
    <div className="mt-4 flex gap-2">
      {onBack && (
        <Button variant="outline" onClick={onBack} className="flex-shrink-0">
          Back
        </Button>
      )}
      <Button fullWidth onClick={onNext} disabled={disabled} loading={loading}>
        {nextLabel}
      </Button>
    </div>
  );
}

function PillSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) {
  return (
    <div className="mb-4">
      <span className="mb-1.5 block text-xs font-medium text-slate-500">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
              value === opt ? "border-teal bg-teal/10 text-teal-dark" : "border-slate-200 text-slate-500"
            }`}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  );
}

function TraitSliders({ traits, onChange }: { traits: TeamFitTraits; onChange: (t: TeamFitTraits) => void }) {
  return (
    <div className="space-y-4">
      {TRAIT_LABELS.map(({ key, label, hint }) => (
        <div key={key}>
          <div className="flex items-baseline justify-between">
            <span className="text-xs font-medium text-navy">{label}</span>
            <span className="text-xs text-teal-dark">{traits[key]}</span>
          </div>
          <p className="text-[11px] text-slate-400">{hint}</p>
          <input
            type="range"
            min={0}
            max={100}
            value={traits[key]}
            onChange={(e) => onChange({ ...traits, [key]: Number(e.target.value) })}
            className="mt-1 w-full accent-teal"
          />
        </div>
      ))}
    </div>
  );
}
